import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  Inbox as InboxIcon,
  Plus,
  Tag,
  UserCheck,
  Check,
  Trash2,
  Save,
  ChevronDown,
  X,
  Search,
  Star,
  SlidersHorizontal,
} from 'lucide-react';
import { InboxList } from '../components/InboxList';
import { InboxRail } from '../components/InboxRail';
import { KnownIssueBanner } from '../components/KnownIssueBanner';
import { ContextMenu } from '../components/ContextMenu';
import { buildConversationMenu } from '../components/conversationMenu';
import { useWpConfigured } from '../hooks/useWpConfigured';
import { NewConversationModal } from '../components/NewConversationModal';
import { TopBar } from '../components/TopBar';
import { useAuth } from '../hooks/useAuth';
import { useChats } from '../hooks/useChats';
import { useUnreadByStatus } from '../hooks/useUnreadByStatus';
import { useInboxViewCounts } from '../hooks/useInboxViewCounts';
import { useFlaggedChats } from '../hooks/useFlaggedChats';
import { useResource } from '../hooks/useResource';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { api, inboxApi, viewsApi, directoryApi, type CustomViewDoc } from '../lib/api';
import { setFaviconBadge, playNotificationPing } from '../lib/notifications';
import { useUiMode } from '../lib/uiMode';
import type { Conversation, ConversationStatus, Inbox, Label, Team } from '../lib/types';
import {
  VIEWS,
  SIMPLE_VIEWS,
  SIMPLE_MORE_VIEWS,
  matchesView,
  viewForChat,
  viewForMode,
  labelForView,
  isInboxView,
  type InboxView,
} from '../lib/inboxViews';

type SortOption = 'newest' | 'oldest' | 'unread' | 'priority';

const SORT_LABELS: Record<SortOption, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  unread: 'Most unread',
  priority: 'Highest priority',
};

const PRIORITY_RANK: Record<Conversation['priority'], number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

interface SavedView {
  // `id` is the server doc id (custom_views). Absent for localStorage-only
  // fallback entries until they sync.
  id?: string;
  name: string;
  view: InboxView;
  scope: 'mine' | 'all';
  labels: string[];
  sort: SortOption;
  /** Single-inbox filter; null/absent = every inbox the agent may see. */
  inboxId?: string | null;
}

// The persisted shape inside the custom_views `filters` record.
interface ViewFilters {
  view: InboxView;
  scope: 'mine' | 'all';
  labels: string[];
  sort: SortOption;
  inboxId: string | null;
}

// The tab, the mine/all scope and the inbox filter are remembered across
// reloads: visiting /settings unmounts this page, and coming back on the
// hard-coded default loses the agent's place. Namespaced per tenant+uid (same
// shape as viewsStorageKey below) — the un-namespaced predecessors leaked one
// agent's filters into the next session on a shared browser, and a remembered
// inbox filter empties every queue with no visible cause.
function prefsKeysFor(tenantCode: string, uid: string) {
  const base = `chatolam:inbox:${tenantCode}:${uid}`;
  return { inbox: `${base}:filter`, view: `${base}:view`, scope: `${base}:scope` };
}
// Deleted on first hydrate rather than migrated: they are shared across accounts
// AND tenants, and the sticky inbox filter one of them held is exactly the bug
// this replaces — carrying it forward would carry the bug forward too.
const LEGACY_PREF_KEYS = ['chatolam:inboxFilter', 'chatolam:inboxView', 'chatolam:inboxScope'];
// Left sidebar (the 'new' layout) collapsed to icons only.
const RAIL_COLLAPSED_KEY = 'chatolam:railCollapsed';

const DEFAULT_VIEW: InboxView = 'waiting';

// One thing currently narrowing the conversation list. Rendered twice: as a
// removable chip above the list, and inline in the empty state so an empty
// queue always says WHY it is empty.
interface ActiveFilter {
  key: string;
  /** Short form for the chip, e.g. `Inbox: Test states`. */
  chip: string;
  /** Reads as a clause after "No … conversations", e.g. `in inbox “Test states”`. */
  phrase: string;
  clear: () => void;
}

function viewFromDoc(doc: CustomViewDoc): SavedView {
  const f = (doc.filters ?? {}) as Partial<ViewFilters> & { status?: string };
  // Back-compat: older saved views stored a `status` (open/pending/…); map it
  // onto the new view model so existing presets still load.
  const v: InboxView =
    f.view ?? (f.status === 'resolved' ? 'resolved' : f.status ? 'all' : 'waiting');
  return {
    id: doc.id,
    name: doc.name,
    view: v,
    scope: f.scope ?? 'all',
    labels: Array.isArray(f.labels) ? f.labels : [],
    sort: f.sort ?? 'newest',
    inboxId: typeof f.inboxId === 'string' ? f.inboxId : null,
  };
}

export function InboxPage() {
  const { chatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();
  const { user, claims, signOut } = useAuth();
  // Simple view hides the secondary rail controls (saved views, starred/team
  // filters, bulk select, the AI-Flagged and parked tabs). Anything currently
  // ACTIVE stays visible in both modes so a filter can never be applied without
  // a control to turn it off. The LABEL filter is deliberately not in that set
  // — see lib/uiMode.ts.
  // `layout === 'rail'` is the third ('new') mode: the scope tabs and the
  // horizontal view-tab bar move into a collapsible left sidebar, from md up.
  // Everything else here — filters, search, list, thread — is untouched, and
  // 'classic' renders exactly what it always did.
  const { advanced, layout } = useUiMode();
  const rail = layout === 'rail';
  // Is the COLLAPSED (three-tab + overflow) status bar the one on screen? Only
  // the classic simple chrome: advanced keeps a tab per queue by definition, and
  // the 'new' layout moved the queues into a vertical rail that has room for all
  // of them. Everything downstream reads the resolved `view`, so neither of
  // those two renders differently than it did before this existed.
  const grouped = !advanced && !rail;
  const wpConfigured = useWpConfigured(claims?.tenantCode);
  // Seeded with the defaults, then hydrated from the per-agent keys once auth
  // has resolved (the namespace needs tenantCode + uid, which are async).
  // The PREFERENCE, which may name a tab the current bar doesn't render (a
  // remembered advanced-only queue, a saved view, the rail); `view` below is the
  // tab actually on screen. Resolving on read rather than rewriting the state on
  // every toggle means flipping the mode can't lose the agent's place.
  const [viewPref, setViewPref] = useState<InboxView>(DEFAULT_VIEW);
  const [scope, setScope] = useState<'mine' | 'all'>('all');
  // Single-inbox filter (null = every inbox the agent's claim allows).
  const [inboxFilter, setInboxFilter] = useState<string | null>(null);
  const [labelFilter, setLabelFilter] = useState<string[]>([]);
  const [teamId, setTeamId] = useState<string | null>(null);
  // Team → agent filter: pick a team, then an agent within it, to see just that
  // agent's conversations. Agent (assignee) is more specific than team, so when
  // an agent is chosen the team filter is dropped in the query.
  const [agentId, setAgentId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('newest');
  const [labelMenuOpen, setLabelMenuOpen] = useState(false);
  // The simple bar's overflow ("More") menu — New (AI) / AI Flagged / All.
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [starredOnly, setStarredOnly] = useState(false);
  // Simple mode only: the narrowing controls (inbox / starred / labels /
  // team-agent) folded behind one "Filters" button. Advanced never opens this —
  // it keeps every control on the row.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [viewsMenuOpen, setViewsMenuOpen] = useState(false);
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(false);
  // Conversation-list width (desktop), drag-resizable and persisted.
  const [listWidth, setListWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 320;
    const v = parseInt(localStorage.getItem('chatolam:listWidth') ?? '', 10);
    return Number.isFinite(v) && v >= 240 && v <= 680 ? v : 320;
  });
  // Sidebar collapsed to icons only ('new' layout). Persisted, like listWidth.
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(RAIL_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // The sidebar's Filters rows are remote controls for the label menu and the
  // team/agent select that already live in the filter row below (inside the
  // "Filters" popover in the default density) — there is no second copy of
  // either menu, so they cannot drift apart.
  const teamSelectRef = useRef<HTMLSelectElement | null>(null);
  // Last global unread total (across all statuses, both tabs) we evaluated for
  // the beep. Scope-independent, so switching the open<->pending tab never
  // rings — only a real new inbound message (which raises the total) does.
  const prevTotalUnreadRef = useRef<number | null>(null);
  // Set by the deep-link auto-tab effect below (declared here so the hydrate
  // effect can see whether a link has already chosen the tab for this chat).
  const autoTabForChatRef = useRef<string | null>(null);
  // Namespace the preferences were last hydrated from (null = never).
  const prefsHydratedRef = useRef<string | null>(null);
  // Gates the persistence effects: writing before the restore has landed would
  // overwrite the remembered values with the defaults they are replacing.
  const [prefsReady, setPrefsReady] = useState(false);

  // The tab actually rendered — the preference above resolved against the bar
  // that is up. In the simple bar 'progress' shows as (and filters like) "Needs
  // you"; in advanced and in the rail this is the preference unchanged, so both
  // behave exactly as they did before the grouping existed.
  const view = viewForMode(viewPref, grouped);

  // Close the toolbar dropdowns on outside click / Escape.
  const labelMenuRef = useOutsideClick<HTMLDivElement>(labelMenuOpen, () => setLabelMenuOpen(false));
  // Wraps the Filters button AND the popover, so clicking the trigger counts as
  // inside and its own toggle owns open/close. The label menu nested inside is
  // likewise "inside", so picking labels can't dismiss the popover under it.
  const filtersMenuRef = useOutsideClick<HTMLDivElement>(filtersOpen, () => setFiltersOpen(false));
  const moreMenuRef = useOutsideClick<HTMLDivElement>(moreMenuOpen, () => setMoreMenuOpen(false));
  const sortMenuRef = useOutsideClick<HTMLDivElement>(sortMenuOpen, () => setSortMenuOpen(false));
  const viewsMenuRef = useOutsideClick<HTMLDivElement>(viewsMenuOpen, () => setViewsMenuOpen(false));

  const { items: labels } = useResource<Label>('labels');
  const { items: teams } = useResource<Team>('teams');
  const { items: inboxes, loading: inboxesLoading } = useResource<Inbox>('inboxes');

  // Which inboxes this agent may pick from. An empty `inboxIds` claim means
  // tenant-wide (see AgentAccessPanel); admins are tenant-wide by rule
  // (firestore.rules agentCanSeeInbox). Anyone else is capped to their claim —
  // picking outside it would just make the listener fail permission-denied.
  const claimInboxIds = claims?.inboxIds;
  const claimInboxKey = (claimInboxIds ?? []).slice().sort().join(',');
  const pickableInboxes = useMemo(() => {
    const unscoped =
      !claimInboxIds ||
      claimInboxIds.length === 0 ||
      claims?.role === 'tenant_admin' ||
      claims?.role === 'super_admin';
    const list = unscoped ? inboxes : inboxes.filter((i) => claimInboxIds!.includes(i.id));
    return [...list].sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxes, claimInboxKey, claims?.role]);

  // localStorage namespace for this agent's remembered tab/scope/inbox. null
  // until auth resolves — nothing is read or written before then, so a signed-
  // out render can never inherit the previous agent's filters.
  const prefsKeys = useMemo(() => {
    if (!claims?.tenantCode || !user?.uid) return null;
    return prefsKeysFor(claims.tenantCode, user.uid);
  }, [claims?.tenantCode, user?.uid]);

  // A stored filter for an inbox that no longer exists (or was revoked) would
  // show a permanently empty list with no clue why — drop it once loaded.
  useEffect(() => {
    if (!inboxFilter || inboxesLoading || pickableInboxes.length === 0) return;
    if (!pickableInboxes.some((i) => i.id === inboxFilter)) selectInbox(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxFilter, inboxesLoading, pickableInboxes]);

  function selectInbox(id: string | null) {
    setInboxFilter(id);
    if (!prefsKeys) return;
    try {
      if (id) localStorage.setItem(prefsKeys.inbox, id);
      else localStorage.removeItem(prefsKeys.inbox);
    } catch {
      /* ignore */
    }
  }

  // Restore the remembered tab / scope / inbox once, as soon as we know who the
  // agent is. The stored view is validated against the known keys rather than
  // cast: a stale (or hand-edited) key naming a tab that no longer exists would
  // wedge the tab bar on nothing selectable. A key that IS known but belongs to
  // the other bar (say a remembered 'wait_internal' while the simple bar is up)
  // is kept as the preference and resolved by viewForMode into the group that
  // contains it — dropping it to the hard default instead would move the agent
  // somewhere they never asked to be. localStorage itself can throw (private
  // mode, blocked storage) — fall back to today's defaults rather than taking
  // the page down with it.
  useEffect(() => {
    // Guarded on the namespace, not a bare boolean: if a different agent (or
    // tenant) takes over this mounted page, they start from THEIR remembered
    // state or from the defaults — never from the last agent's leftovers.
    if (!prefsKeys || prefsHydratedRef.current === prefsKeys.view) return;
    prefsHydratedRef.current = prefsKeys.view;
    let storedView: InboxView = DEFAULT_VIEW;
    let storedScope: 'mine' | 'all' = 'all';
    let storedInbox: string | null = null;
    try {
      for (const k of LEGACY_PREF_KEYS) localStorage.removeItem(k);
      const v = localStorage.getItem(prefsKeys.view);
      if (v && isInboxView(v)) storedView = v;
      const s = localStorage.getItem(prefsKeys.scope);
      if (s === 'mine' || s === 'all') storedScope = s;
      storedInbox = localStorage.getItem(prefsKeys.inbox) || null;
    } catch {
      /* ignore */
    }
    // A deep link may already have picked the tab for the open chat — don't
    // yank the agent off it.
    if (!autoTabForChatRef.current) setViewPref(storedView);
    setScope(storedScope);
    setInboxFilter(storedInbox);
    // Batched with the setters above, so the first render that may persist
    // already holds the restored values.
    setPrefsReady(true);
  }, [prefsKeys]);

  // Scope is only ever moved by the agent (the two scope tabs, the sidebar, a
  // loaded saved view), so an effect can cover every path.
  useEffect(() => {
    if (!prefsKeys || !prefsReady) return;
    try {
      localStorage.setItem(prefsKeys.scope, scope);
    } catch {
      /* ignore */
    }
  }, [scope, prefsKeys, prefsReady]);

  // The TAB is not like that: the deep-link effect below moves it too, on the
  // machine's behalf. Persisting that write relocated the agent's default queue
  // permanently after a single link click, so the write lives here — on the one
  // path every user-initiated tab change goes through (tab bar, sidebar rail,
  // saved view, "Needs you now") — instead of in an effect that cannot tell the
  // two apart.
  // Resolved through viewForMode first: a saved view (or the sidebar) can name a
  // queue the current bar doesn't render, and selecting a tab that isn't there
  // leaves the bar with nothing highlighted over a list that IS filtered. In
  // advanced and in the rail this resolves to the key it was handed, unchanged.
  function chooseView(v: InboxView) {
    const next = viewForMode(v, grouped);
    setViewPref(next);
    if (!prefsKeys) return;
    try {
      localStorage.setItem(prefsKeys.view, next);
    } catch {
      /* ignore */
    }
  }

  // Query scope: the picked inbox, else the claim's set, else unfiltered.
  const effectiveInboxIds = useMemo(
    () => (inboxFilter ? [inboxFilter] : claimInboxIds && claimInboxIds.length > 0 ? claimInboxIds : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inboxFilter, claimInboxKey],
  );

  const { chats, loading, hasMore, loadMore, labelSearchDegraded } = useChats({
    // Resolved is fetched server-side; the active views (New/Open/In-progress)
    // and All fetch broadly and classify client-side (see matchesView) — the
    // buckets mix status + aiHandled + assignee, which isn't a single Firestore
    // filter. Larger window so classification has enough to show.
    status: view === 'resolved' ? 'resolved' : undefined,
    // Active tabs scope the fetch to non-resolved statuses server-side, so a
    // large RESOLVED archive (Freshchat import) can't fill the window and hide
    // live chats. 'All' intentionally has no scope (shows everything). The two
    // parked tabs ride the SAME array — a parked chat is a 'snoozed' one, which
    // is already in it, so this reuses the existing composite index. The two
    // grouped tabs are unions of those buckets, so every member status is
    // already inside this 'in' filter: same array, same index, no new one.
    statusIn:
      view === 'ai' ||
      view === 'waiting' ||
      view === 'progress' ||
      view === 'wait_customer' ||
      view === 'wait_internal' ||
      view === 'needs_you' ||
      view === 'waiting_any'
        ? (['open', 'pending', 'snoozed'] as const as ConversationStatus[])
        : undefined,
    // Show the latest 20; the agent clicks "Load more" for the next page.
    limit: 20,
    inboxIds: effectiveInboxIds,
    // A chosen agent filters by assignee; else the "Assigned to me" scope does.
    assigneeUid: agentId ?? (scope === 'mine' ? user?.uid : undefined),
    labels: labelFilter,
    // Agent is the more specific filter — don't also constrain by team.
    teamId: agentId ? undefined : teamId ?? undefined,
  });
  // Dedicated AI-Flagged worklist source (F11) — powers both the flagged tab
  // list and its badge, so they can never disagree.
  const flaggedChats = useFlaggedChats(effectiveInboxIds);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; chat: Conversation } | null>(null);

  const viewsStorageKey = useMemo(() => {
    if (!claims?.tenantCode || !user?.uid) return null;
    return `chatolam:views:${claims.tenantCode}:${user.uid}`;
  }, [claims?.tenantCode, user?.uid]);

  // Load saved views: localStorage first (instant), then the server as the
  // source of truth. Server views are filtered to this agent (owner == uid)
  // and mirrored back to localStorage as an offline cache.
  useEffect(() => {
    if (!viewsStorageKey) return;
    try {
      const raw = localStorage.getItem(viewsStorageKey);
      if (raw) setSavedViews(JSON.parse(raw) as SavedView[]);
    } catch {
      /* ignore */
    }
    let cancelled = false;
    viewsApi
      .list()
      .then((r) => {
        if (cancelled || !user?.uid) return;
        const mine = r.items.filter((d) => d.owner === user.uid).map(viewFromDoc);
        setSavedViews(mine);
        try {
          localStorage.setItem(viewsStorageKey, JSON.stringify(mine));
        } catch {
          /* ignore */
        }
      })
      .catch((e) => {
        // Offline / endpoint error: keep the localStorage copy already loaded.
        console.error('load saved views failed', e);
      });
    return () => {
      cancelled = true;
    };
  }, [viewsStorageKey, user?.uid]);

  // Notification permission state
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    setNotifEnabled(Notification.permission === 'granted');
  }, []);

  // Sorted chats (client-side)
  const sortedChats = useMemo(() => {
    const arr = [...chats];
    switch (sort) {
      case 'oldest':
        arr.sort((a, b) => a.lastMessageAt - b.lastMessageAt);
        break;
      case 'unread':
        arr.sort((a, b) => (b.unreadCount ?? 0) - (a.unreadCount ?? 0) || b.lastMessageAt - a.lastMessageAt);
        break;
      case 'priority':
        arr.sort(
          (a, b) =>
            (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
            b.lastMessageAt - a.lastMessageAt
        );
        break;
      case 'newest':
      default:
        arr.sort((a, b) => b.lastMessageAt - a.lastMessageAt);
    }
    return arr;
  }, [chats, sort]);

  // Client-side conversation search: filters the loaded list by contact
  // name/email/phone, last-message preview, or contact id.
  const visibleChats = useMemo(() => {
    // The AI-Flagged tab uses its own dedicated source (any status, keyed on the
    // aiFlagged marker) so the list and the badge are the SAME set — see
    // useFlaggedChats. Other tabs classify the loaded window client-side.
    let arr = view === 'flagged'
      ? flaggedChats.slice()
      : sortedChats.filter((c) => matchesView(c, view));
    if (starredOnly) arr = arr.filter((c) => c.starred);
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(
      (c) =>
        (c.contactName ?? '').toLowerCase().includes(q) ||
        (c.contactEmail ?? '').toLowerCase().includes(q) ||
        (c.contactPhone ?? '').toLowerCase().includes(q) ||
        (c.lastMessagePreview ?? '').toLowerCase().includes(q) ||
        c.contactId.toLowerCase().includes(q)
    );
  }, [sortedChats, flaggedChats, search, starredOnly, view]);

  // Deep-link: /inbox/:chatId opens the chat in the panel, but the active tab is
  // independent state (seeded from the remembered tab) and may not contain it (e.g. a human took
  // it over → 'progress', so it's filtered out of the Open list). Resolve the chat
  // (directly, in case it's outside the loaded window) and switch to the tab whose
  // filter contains it — once per chatId, and only when the current tab doesn't
  // already contain it (so it never fights an agent who manually changes tabs).
  // Deliberately raw setViewPref, not chooseView: this tab was chosen by a link,
  // not by the agent, and must not become their remembered default queue. Both
  // the "does the current tab already hold it" test and the target are resolved
  // for the bar that is up, so a 'progress' chat lands on a tab that is actually
  // VISIBLE ("Needs you" in the simple bar) rather than on a hidden one.
  useEffect(() => {
    if (!chatId) {
      autoTabForChatRef.current = null;
      return;
    }
    if (autoTabForChatRef.current === chatId) return;
    const known = chats.find((c) => c.id === chatId);
    if (known) {
      autoTabForChatRef.current = chatId;
      setViewPref((v) =>
        matchesView(known, viewForMode(v, grouped)) ? v : viewForChat(known, grouped),
      );
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'chats', chatId))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const c = { id: snap.id, ...(snap.data() as Omit<Conversation, 'id'>) } as Conversation;
        autoTabForChatRef.current = chatId;
        setViewPref((v) => (matchesView(c, viewForMode(v, grouped)) ? v : viewForChat(c, grouped)));
      })
      .catch((e) => console.error('deep-link tab resolve failed', e));
    return () => {
      cancelled = true;
    };
  }, [chatId, chats, grouped]);

  // Once the deep-linked row is in the filtered list, scroll it into view.
  useEffect(() => {
    if (!chatId) return;
    if (!visibleChats.some((c) => c.id === chatId)) return;
    const el = document.querySelector<HTMLElement>(`[data-chat-id="${CSS.escape(chatId)}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [chatId, visibleChats]);

  // Total unread (favicon badge) — independent of the current view.
  const { totalUnread } = useUnreadByStatus(claims?.inboxIds);
  // Live counts per view tab (New / Open / In-progress). Follows the inbox
  // picker so the badges always describe the rows below them; the favicon /
  // ping total above deliberately does NOT (it's the global "something
  // arrived" signal, and must survive filtering).
  const viewCountsRaw = useInboxViewCounts(effectiveInboxIds);
  // The AI-Flagged badge counts the SAME dedicated worklist the tab lists, so
  // the number can never disagree with the rows (F11).
  const viewCounts = useMemo(
    () => ({ ...viewCountsRaw, flagged: flaggedChats.length }),
    [viewCountsRaw, flaggedChats],
  );

  // Unread conversations sitting in the Open (waiting) tab — or, when the simple
  // bar has folded that queue into "Needs you", in whichever of the two is on
  // screen. Derived from the loaded window (`chats`) through the SAME classifier
  // the list uses, so it's distinct from the total Open count (which counts ALL
  // waiting chats) and can't count rows the tab isn't showing. For the advanced
  // 'waiting' tab this is the identical filter it always was.
  const openUnreadCount = useMemo(
    () => chats.filter((c) => matchesView(c, view) && (c.unreadCount ?? 0) > 0).length,
    [chats, view]
  );

  // Blink the Open tab badge red for ~5s whenever the waiting count GROWS — a
  // fresh escalation landed. Driven off the live `viewCounts.waiting` so it
  // fires regardless of which tab is currently selected.
  const [openBadgeBlink, setOpenBadgeBlink] = useState(false);
  const prevWaitingRef = useRef<number | null>(null);
  const blinkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const prev = prevWaitingRef.current;
    const cur = viewCounts.waiting;
    prevWaitingRef.current = cur;
    if (prev === null) return; // first run: seed baseline, don't blink
    if (cur <= prev) return; // resolved / picked up / no change → no blink
    setOpenBadgeBlink(true);
    playNotificationPing(); // pair with the existing alert ping
    if (blinkTimerRef.current) clearTimeout(blinkTimerRef.current);
    blinkTimerRef.current = setTimeout(() => setOpenBadgeBlink(false), 5000);
  }, [viewCounts.waiting]);
  useEffect(() => () => {
    if (blinkTimerRef.current) clearTimeout(blinkTimerRef.current);
  }, []);

  // uid → display name, so list rows can show who each conversation is assigned to.
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!claims?.tenantCode) return;
    let cancelled = false;
    directoryApi
      .listAgentsDirectory(claims.tenantCode)
      .then((r) => {
        if (cancelled) return;
        const m: Record<string, string> = {};
        for (const a of r.items) m[a.uid] = a.displayName;
        setAgentNames(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [claims?.tenantCode]);

  // The list shows the latest 20; the agent pages the rest with an explicit
  // "Load more" button (see InboxList). No automatic paging.

  useEffect(() => {
    setFaviconBadge(totalUnread);
    document.title = totalUnread > 0 ? `(${totalUnread}) ChatOlam` : 'ChatOlam';
  }, [totalUnread]);

  // Beep + browser notification when the GLOBAL unread total goes up. Driven
  // off the tab-independent total (useUnreadByStatus), NOT the filtered list,
  // so switching open<->pending — which swaps the visible chats but not the
  // total — never rings. Only a genuine new inbound message does. The
  // notification's detail is best-effort from whatever chats are loaded.
  useEffect(() => {
    const prev = prevTotalUnreadRef.current;
    prevTotalUnreadRef.current = totalUnread;
    if (prev === null) return; // first run: seed the baseline, don't ring
    if (totalUnread <= prev) return; // read, tab switch, or no change → silent
    playNotificationPing();
    if (notifEnabled && typeof Notification !== 'undefined') {
      const c = chats.find((x) => (x.unreadCount ?? 0) > 0);
      if (c) {
        try {
          const body = `${c.contactName || c.contactEmail || c.contactPhone || c.contactId.slice(0, 20)}: ${c.lastMessagePreview || ''}`.slice(0, 140);
          const n = new Notification('New message', { body, tag: c.id });
          n.onclick = () => {
            window.focus();
            navigate(`/inbox/${c.id}`);
          };
        } catch {
          /* ignore */
        }
      }
    }
  }, [totalUnread, chats, notifEnabled, navigate]);

  function toggleSelect(id: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onRowContextMenu(e: ReactMouseEvent, chat: Conversation) {
    if (!user || !claims?.tenantCode) return;
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, chat });
  }

  // Drag the divider between the conversation list and the chat pane.
  function startResize(e: ReactMouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = listWidth;
    let latest = startW;
    function onMove(ev: globalThis.MouseEvent) {
      latest = Math.min(680, Math.max(240, startW + ev.clientX - startX));
      setListWidth(latest);
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      try {
        localStorage.setItem('chatolam:listWidth', String(latest));
      } catch {
        /* ignore */
      }
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }

  async function bulkResolve() {
    if (!user || !claims?.tenantCode || selected.size === 0) return;
    if (!confirm(`Resolve ${selected.size} conversations?`)) return;
    setBulkBusy(true);
    try {
      await api.bulkUpdateChats(claims.tenantCode, user.uid, [...selected], {
        action: 'status',
        value: 'resolved',
      });
      setSelected(new Set());
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkAssignToMe() {
    if (!user || !claims?.tenantCode || selected.size === 0) return;
    setBulkBusy(true);
    try {
      await api.bulkUpdateChats(claims.tenantCode, user.uid, [...selected], {
        action: 'assign',
        value: user.uid,
      });
      setSelected(new Set());
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkLabel() {
    if (!user || !claims?.tenantCode || selected.size === 0) return;
    const label = prompt('Label name to add:');
    if (!label) return;
    setBulkBusy(true);
    try {
      await api.bulkUpdateChats(claims.tenantCode, user.uid, [...selected], {
        action: 'label_add',
        value: label,
      });
      setSelected(new Set());
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkDelete() {
    if (!user || !claims?.tenantCode || selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} conversations? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      await inboxApi.bulkDeleteChats(claims.tenantCode, user.uid, [...selected]);
      setSelected(new Set());
    } finally {
      setBulkBusy(false);
    }
  }

  function toggleLabelFilter(name: string) {
    setLabelFilter((cur) =>
      cur.includes(name) ? cur.filter((l) => l !== name) : [...cur, name]
    );
  }

  function cacheViews(next: SavedView[]) {
    if (!viewsStorageKey) return;
    try {
      localStorage.setItem(viewsStorageKey, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }

  async function saveCurrentView() {
    if (!viewsStorageKey || !user?.uid) return;
    const name = prompt('Name this view:')?.trim();
    if (!name) return;
    const filters: ViewFilters = { view, scope, labels: labelFilter, sort, inboxId: inboxFilter };
    // If a same-named view exists, replace it (delete old server doc first).
    const existing = savedViews.find((v) => v.name === name);
    try {
      if (existing?.id) await viewsApi.remove(existing.id).catch(() => {});
      const { id } = await viewsApi.create({
        name,
        filters: filters as unknown as Record<string, unknown>,
        owner: user.uid,
      });
      const saved: SavedView = { id, name, ...filters };
      const next = [...savedViews.filter((v) => v.name !== name), saved];
      setSavedViews(next);
      cacheViews(next);
    } catch (e) {
      // Server unavailable: persist locally so the agent doesn't lose the view.
      console.error('save view failed', e);
      const saved: SavedView = { name, ...filters };
      const next = [...savedViews.filter((v) => v.name !== name), saved];
      setSavedViews(next);
      cacheViews(next);
    }
  }

  function loadView(v: SavedView) {
    chooseView(v.view);
    setScope(v.scope);
    setLabelFilter(v.labels ?? []);
    setSort(v.sort);
    selectInbox(v.inboxId ?? null);
    setViewsMenuOpen(false);
  }

  async function deleteView(name: string) {
    if (!viewsStorageKey) return;
    const target = savedViews.find((v) => v.name === name);
    const next = savedViews.filter((v) => v.name !== name);
    setSavedViews(next);
    cacheViews(next);
    if (target?.id) await viewsApi.remove(target.id).catch((e) => console.error('delete view failed', e));
  }

  async function toggleNotifications() {
    if (typeof Notification === 'undefined') {
      alert('Browser notifications are not supported.');
      return;
    }
    if (notifEnabled) {
      // Can't programmatically revoke; just stop firing.
      setNotifEnabled(false);
      return;
    }
    if (Notification.permission === 'granted') {
      setNotifEnabled(true);
      return;
    }
    const perm = await Notification.requestPermission();
    setNotifEnabled(perm === 'granted');
  }

  // Keyboard shortcuts
  useEffect(() => {
    if (!user || !claims?.tenantCode) return;

    function isTypingTarget(t: EventTarget | null): boolean {
      if (!t) return false;
      const el = t as HTMLElement;
      const tag = el.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
      if (el.isContentEditable) return true;
      return false;
    }

    function currentIndex(): number {
      return visibleChats.findIndex((c) => c.id === chatId);
    }

    async function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key;
      if (key === '/' && !isTypingTarget(e.target)) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (key === 'Escape') {
        setSelected(new Set());
        setLabelMenuOpen(false);
        setSortMenuOpen(false);
        setViewsMenuOpen(false);
        setMoreMenuOpen(false);
        setFiltersOpen(false);
        return;
      }
      if (isTypingTarget(e.target)) return;
      if (key === 'j' || key === 'k') {
        e.preventDefault();
        if (visibleChats.length === 0) return;
        const idx = currentIndex();
        let next = key === 'j' ? idx + 1 : idx - 1;
        if (idx < 0) next = 0;
        next = Math.max(0, Math.min(visibleChats.length - 1, next));
        const target = visibleChats[next];
        if (target) navigate(`/inbox/${target.id}`);
        return;
      }
      if (!chatId) return;
      if (key === 'r') {
        e.preventDefault();
        await api.setStatus(claims!.tenantCode!, chatId, user!.uid, 'resolved').catch(() => {});
        return;
      }
      if (key === 's') {
        e.preventDefault();
        await api.snooze(claims!.tenantCode!, chatId, user!.uid, Date.now() + 60 * 60 * 1000).catch(() => {});
        return;
      }
      if (key === 'a') {
        e.preventDefault();
        await api.assign(claims!.tenantCode!, chatId, user!.uid, user!.uid).catch(() => {});
        return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visibleChats, chatId, user, claims, navigate]);

  // Everything that is currently narrowing the list, in one place. Two of these
  // (the inbox picker and the mine/all scope) are remembered across reloads and
  // are easy to leave parked and forget; a queue filtered down to nothing then
  // looks identical to a queue that is genuinely empty — badge included, since
  // the badges follow the same inbox filter. Rendered as removable chips above
  // the list AND named in the empty state, so that can't happen again.
  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const out: ActiveFilter[] = [];
    if (inboxFilter) {
      const name = pickableInboxes.find((i) => i.id === inboxFilter)?.name || inboxFilter;
      out.push({
        key: 'inbox',
        chip: `Inbox: ${name}`,
        phrase: `in inbox “${name}”`,
        clear: () => selectInbox(null),
      });
    }
    if (scope === 'mine') {
      out.push({
        key: 'scope',
        chip: 'Assigned to me',
        phrase: 'assigned to you',
        clear: () => setScope('all'),
      });
    }
    if (agentId) {
      const name = agentNames[agentId] || agentId;
      out.push({
        key: 'agent',
        chip: `Agent: ${name}`,
        phrase: `assigned to ${name}`,
        clear: () => setAgentId(null),
      });
    } else if (teamId) {
      const name = teams.find((t) => t.id === teamId)?.name || teamId;
      out.push({
        key: 'team',
        chip: `Team: ${name}`,
        phrase: `owned by team “${name}”`,
        clear: () => setTeamId(null),
      });
    }
    if (starredOnly) {
      out.push({
        key: 'starred',
        chip: 'Starred',
        phrase: 'starred',
        clear: () => setStarredOnly(false),
      });
    }
    for (const l of labelFilter) {
      out.push({
        key: `label:${l}`,
        chip: l,
        phrase: `labelled “${l}”`,
        clear: () => toggleLabelFilter(l),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxFilter, pickableInboxes, scope, agentId, agentNames, teamId, teams, starredOnly, labelFilter]);

  // "Show me everything again" — the narrowing filters plus the search box, but
  // NOT the tab: the empty state names the tab, so resetting it too would answer
  // a question the agent didn't ask.
  function clearAllFilters() {
    selectInbox(null);
    setScope('all');
    setAgentId(null);
    setTeamId(null);
    setStarredOnly(false);
    setLabelFilter([]);
    setSearch('');
  }

  // No badge on Resolved (none needed) or All (its live count listener excludes
  // resolved/snoozed, so it would undercount what the All view actually shows).
  function countForView(key: InboxView): number | null {
    return key === 'resolved' || key === 'all' ? null : viewCounts[key];
  }

  // One tab button, drawn by BOTH bars — the full eight and the collapsed three
  // — so the badge rule, the blink, the tooltip and the title fallback can't
  // drift between them.
  function renderViewTab(v: { key: InboxView; label: string; hint: string }) {
    const isSel = view === v.key;
    const count = countForView(v.key);
    // The blink follows the Open (waiting) queue to wherever it is rendered: its
    // own tab in the full bar, folded into "Needs you" in the collapsed one.
    const blink = (v.key === 'waiting' || v.key === 'needs_you') && openBadgeBlink;
    return (
      <button
        key={v.key}
        onClick={() => chooseView(v.key)}
        // Native title = always-works fallback (never clipped) in
        // addition to the styled hover tooltip below.
        title={v.hint}
        className={`group relative px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
          isSel
            ? 'text-primary-700 bg-primary-50 border-b-2 border-primary-600'
            : 'text-gray-500 hover:bg-gray-50 border-b-2 border-transparent'
        }`}
      >
        {v.label}
        {count != null && count > 0 && (
          <span
            className={`text-[9px] font-semibold rounded-full px-1.5 py-0.5 min-w-[16px] text-center ${
              blink
                ? 'badge-blink-red'
                : isSel
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {count}
          </span>
        )}
        {/* Hover tooltip describing what the tab holds. */}
        <span
          role="tooltip"
          className="pointer-events-none absolute top-full left-1/2 z-30 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[10px] font-normal normal-case text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
        >
          {v.hint}
        </span>
      </button>
    );
  }

  // The four NARROWING controls — inbox, starred, labels, team/agent — drawn by
  // BOTH chromes from one definition: advanced lays them out along the filter
  // row exactly as it always has, the default bar stacks them inside the
  // "Filters" popover. Only the box around them changes; they are the same
  // controls wired to the same state, so the two can't drift apart.
  // Sort is deliberately NOT one of them — see the row below.
  function renderFilterControls(stacked: boolean) {
    // In the popover each control owns the full width; on the row they stay
    // capped so one long inbox (or team) name can't eat it.
    const width = stacked ? 'w-full' : 'max-w-[180px]';
    return (
      <>
        {/* Which inbox to show. Hidden when there's nothing to choose
            between. "All inboxes" = every inbox the agent's claim allows,
            which is the pre-existing behaviour. */}
        {pickableInboxes.length > 1 && (
          <select
            value={inboxFilter ?? ''}
            onChange={(e) => selectInbox(e.target.value || null)}
            className={`px-2 py-1 border rounded ${width} ${
              inboxFilter
                ? 'bg-primary-50 border-primary-300 text-primary-700 font-medium'
                : 'bg-white border-gray-300 text-gray-700'
            }`}
            title="Show conversations from one inbox"
          >
            <option value="">All inboxes</option>
            {pickableInboxes.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name || i.id}
              </option>
            ))}
          </select>
        )}
        {/* Starred used to be advanced-only unless it was already on (a filter
            must never be applied without a control to lift it). The popover is
            what that gate was buying — space on the row — so the default bar
            can afford the control itself now. Advanced renders it exactly where
            it always did. */}
        <button
          type="button"
          onClick={() => setStarredOnly((v) => !v)}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${
            stacked ? 'w-full' : ''
          } ${
            starredOnly
              ? 'bg-amber-100 border-amber-300 text-amber-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
          title="Show only starred conversations"
        >
          <Star className={`w-3 h-3 ${starredOnly ? 'fill-amber-500 text-amber-500' : ''}`} />
          Starred
        </button>
        {/* Labels are a first-class filter in BOTH densities. Hiding it
            behind 'advanced' (the default is novice) is why tagging was
            reported as missing while the AI was already applying labels to
            every conversation. */}
        <div className={`relative ${stacked ? 'w-full' : ''}`} ref={labelMenuRef}>
          <button
            type="button"
            onClick={() => setLabelMenuOpen((v) => !v)}
            className={`inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 ${
              stacked ? 'w-full' : ''
            }`}
          >
            <Tag className="w-3 h-3" />
            Labels
            {labelFilter.length > 0 && (
              <span className="ml-1 bg-primary-600 text-white text-[9px] rounded-full px-1.5">
                {labelFilter.length}
              </span>
            )}
            <ChevronDown className="w-3 h-3" />
          </button>
          {labelMenuOpen && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-20 min-w-[200px] max-h-64 overflow-y-auto py-1">
              {labels.length === 0 ? (
                <div className="px-3 py-2 text-gray-500">No labels defined.</div>
              ) : (
                labels.map((l) => {
                  const checked = labelFilter.includes(l.name);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => toggleLabelFilter(l.name)}
                      className="flex items-center w-full px-3 py-1.5 hover:bg-gray-50 text-left"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        readOnly
                        className="mr-2 w-3 h-3"
                      />
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-2"
                        style={{ background: l.color }}
                      />
                      <span className="flex-1">{l.name}</span>
                    </button>
                  );
                })
              )}
              {labelFilter.length > 0 && (
                <>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    type="button"
                    onClick={() => setLabelFilter([])}
                    className="block w-full text-left px-3 py-1.5 text-gray-500 hover:bg-gray-50"
                  >
                    Clear all
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        {/* One team→agent filter: teams are group headers, their agents
            nested beneath. Pick a whole team or a single agent. Always
            rendered wherever the mode puts it — the 'new' layout's sidebar
            "Teams" row focuses THIS select rather than duplicating the menu,
            so a mode that didn't render it would make that row a no-op. */}
        <select
          ref={teamSelectRef}
          value={agentId ? `agent:${agentId}` : teamId ? `team:${teamId}` : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v.startsWith('agent:')) { setAgentId(v.slice(6)); setTeamId(null); }
            else if (v.startsWith('team:')) { setTeamId(v.slice(5)); setAgentId(null); }
            else { setTeamId(null); setAgentId(null); }
          }}
          className={`px-2 py-1 bg-white border border-gray-300 rounded text-gray-700 ${width}`}
          title="Filter by team or agent"
        >
          <option value="">All conversations</option>
          {teams.map((t) => {
            const uids = (t.agentUids ?? [])
              .filter((u) => agentNames[u])
              .sort((a, b) => (agentNames[a] ?? '').localeCompare(agentNames[b] ?? ''));
            return (
              <optgroup key={t.id} label={t.name}>
                <option value={`team:${t.id}`}>All of {t.name}</option>
                {uids.map((u) => (
                  <option key={u} value={`agent:${u}`}>{agentNames[u]}</option>
                ))}
              </optgroup>
            );
          })}
        </select>
      </>
    );
  }

  const role = claims?.role;
  const isSuperAdmin = role === 'super_admin';

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar
        tenantCode={claims?.tenantCode ?? ''}
        userEmail={user?.email ?? undefined}
        onSignOut={signOut}
        searchInputRef={searchInputRef}
        notificationsEnabled={notifEnabled}
        onToggleNotifications={toggleNotifications}
      />

      {/* Everything under the fixed 40px TopBar, as a column: an operator-declared
          known issue takes its natural height at the top and the panes below
          shrink to fit, instead of the banner pushing the whole viewport-tall
          grid down into a scrollbar. */}
      <div className="pt-10 h-screen flex flex-col overflow-hidden">
        <KnownIssueBanner />
        <div
          className={`flex-1 min-h-0 grid grid-cols-1 ${
            rail ? 'md:grid-cols-[var(--rail-w)_var(--list-w)_1fr]' : 'md:grid-cols-[var(--list-w)_1fr]'
          } grid-rows-[minmax(0,1fr)] overflow-hidden`}
          style={
            { '--list-w': `${listWidth}px`, '--rail-w': railCollapsed ? '46px' : '176px' } as CSSProperties
          }
        >
        {/* 'new' layout only, and desktop only — the sidebar is `hidden md:flex`,
            so mobile keeps the classic two-pane chrome in all three modes. */}
        {rail && (
          <InboxRail
            view={view}
            onView={chooseView}
            scope={scope}
            onScope={setScope}
            starredOnly={starredOnly}
            onStarred={setStarredOnly}
            counts={viewCounts}
            labelCount={labelFilter.length}
            // The rail's Filters rows are remote controls for the label menu and
            // the team select, which now sit inside the "Filters" popover (the
            // rail is a 'new'-mode layout, so it is never the advanced row).
            // Open the popover first, or both rows would be no-ops.
            onOpenLabels={() => {
              setFiltersOpen(true);
              setLabelMenuOpen((v) => !v);
            }}
            teamActive={!!(teamId || agentId)}
            onOpenTeams={() => {
              setFiltersOpen(true);
              // The select may only just have been mounted by the line above —
              // focus once React has committed it.
              setTimeout(() => teamSelectRef.current?.focus(), 0);
            }}
            collapsed={railCollapsed}
            onToggleCollapse={() =>
              setRailCollapsed((v) => {
                const next = !v;
                try {
                  localStorage.setItem(RAIL_COLLAPSED_KEY, next ? '1' : '0');
                } catch {
                  /* ignore */
                }
                return next;
              })
            }
            needsYouNow={view === 'waiting' && scope === 'all' && !starredOnly}
            onNeedsYouNow={() => {
              chooseView('waiting');
              setScope('all');
              setStarredOnly(false);
            }}
          />
        )}
        <aside
          className={`relative ${chatId ? 'hidden md:flex' : 'flex'} border-r border-gray-200 bg-white flex-col min-h-0`}
        >
          {/* Drag handle to resize the conversation list (desktop only) */}
          <div
            onMouseDown={startResize}
            className="hidden md:block absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize hover:bg-primary-300/70 active:bg-primary-400"
            title="Drag to resize"
          />
          {/* Scope tabs + Save view. In the 'new' layout the two scope tabs move
              into the sidebar's Focus section from md up; "New" and the saved-
              views menu stay here either way. Below md the sidebar doesn't
              render, so the tabs stay exactly where they have always been. */}
          <div className={`flex border-b border-gray-200 items-stretch ${rail ? 'md:justify-end' : ''}`}>
            {(['all', 'mine'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                  rail ? 'md:hidden' : ''
                } ${
                  scope === s
                    ? 'text-primary-700 bg-primary-50 border-b-2 border-primary-600'
                    : 'text-gray-600 hover:bg-gray-50 border-b-2 border-transparent'
                }`}
              >
                {/* "All" is an ASSIGNEE scope, not an inbox one — the inbox
                    picker in the filter row below owns that axis now. */}
                {s === 'all' ? 'All conversations' : 'Assigned to me'}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setComposeOpen(true)}
              className="border-l border-gray-200 px-2.5 text-xs font-medium text-primary-700 hover:bg-primary-50 flex items-center gap-1"
              title="Start a new outbound conversation"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">New</span>
            </button>
            <div
              className={`relative border-l border-gray-200 ${advanced ? '' : 'hidden'}`}
              ref={viewsMenuRef}
            >
              <button
                type="button"
                onClick={() => setViewsMenuOpen((v) => !v)}
                className="h-full px-2 text-xs text-gray-600 hover:bg-gray-50 flex items-center gap-1"
                title="Saved views"
              >
                <Save className="w-3.5 h-3.5" />
                <ChevronDown className="w-3 h-3" />
              </button>
              {viewsMenuOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-20 min-w-[200px] py-1 text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      saveCurrentView();
                      setViewsMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-1.5 hover:bg-gray-50 text-primary-700 font-medium"
                  >
                    + Save current view
                  </button>
                  {savedViews.length > 0 && <div className="border-t border-gray-100 my-1" />}
                  {savedViews.map((v) => (
                    <div
                      key={v.name}
                      className="flex items-center justify-between hover:bg-gray-50 group"
                    >
                      <button
                        type="button"
                        onClick={() => loadView(v)}
                        className="flex-1 text-left px-3 py-1.5"
                      >
                        {v.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteView(v.name)}
                        className="px-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100"
                        title="Delete preset"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Conversation views by who's handling it. Total case count per tab;
              Resolved has no count. Two bars, one tab renderer: ADVANCED (and
              the rail's below-md fallback) keeps every tab it has ever had,
              while the DEFAULT bar shows Needs you / Waiting / Done with the
              rest behind "More" — eight tabs wrapped onto four lines before an
              agent had read a single message. The grouped tabs filter, count and
              deep-link off the same buckets the eight do (see lib/inboxViews),
              so the bars can't disagree about where a conversation lives. */}
          {/* flex-wrap (not overflow-x-auto): an x-scroll container forces
              overflow-y to clip, which hid the top-full hover tooltip. Wrapping
              keeps the hint visible and fits the longer "(AI)/(Pending)/(Human)"
              labels without a scrollbar. */}
          {/* Relocated to the sidebar's Queues section in the 'new' layout, but
              only from md up, where that sidebar exists. */}
          <div className={`flex ${rail ? 'md:hidden' : ''} flex-wrap border-b border-gray-200`}>
            {grouped
              ? [
                  ...SIMPLE_VIEWS,
                  // Escape hatch, same rule the full bar has always used: the
                  // tab that is CURRENTLY selected never disappears — here that
                  // is an overflow queue the agent picked from "More".
                  ...SIMPLE_MORE_VIEWS.filter((v) => v.key === view),
                ].map(renderViewTab)
              : VIEWS.map((v) => {
                  const isSel = view === v.key;
                  // AI-Flagged is an AI-review worklist — advanced only (unless
                  // it's the tab currently open, which must stay reachable).
                  if (!advanced && v.key === 'flagged' && !isSel) return null;
                  // Same for the two parked queues: triage tooling, not part of
                  // the simple New/Open/In-progress flow. Same escape hatch.
                  if (!advanced && (v.key === 'wait_customer' || v.key === 'wait_internal') && !isSel)
                    return null;
                  return renderViewTab(v);
                })}
            {grouped && (
              <div className="relative ms-auto" ref={moreMenuRef}>
                <button
                  type="button"
                  onClick={() => setMoreMenuOpen((v) => !v)}
                  title="Other queues — AI-handled, flagged for review, everything"
                  aria-expanded={moreMenuOpen}
                  className="h-full px-3 py-2 text-xs font-medium whitespace-nowrap text-gray-500 hover:bg-gray-50 border-b-2 border-transparent flex items-center gap-1"
                >
                  More
                  <ChevronDown className="w-3 h-3" />
                </button>
                {moreMenuOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-30 min-w-[180px] py-1 text-xs">
                    {SIMPLE_MORE_VIEWS.map((v) => {
                      const count = countForView(v.key);
                      return (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => {
                            chooseView(v.key);
                            setMoreMenuOpen(false);
                          }}
                          title={v.hint}
                          className={`flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 ${
                            view === v.key ? 'text-primary-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          <span className="flex-1">{v.label}</span>
                          {count != null && count > 0 && (
                            <span className="text-[9px] font-semibold rounded-full px-1.5 py-0.5 min-w-[16px] text-center bg-gray-200 text-gray-500">
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Open-tab unread tally — distinct from the total Open count above.
              Only shown when the Open view (or the "Needs you" tab that folds it
              in) is active and some of its conversations are unread by the
              agent. */}
          {(view === 'waiting' || view === 'needs_you') && openUnreadCount > 0 && (
            <div className="border-b border-gray-200 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800">
              {openUnreadCount} unread
            </div>
          )}

          {/* Inbox picker + Label filter + Sort. ADVANCED keeps all four
              narrowing controls on the row, exactly as they have always been
              laid out. The DEFAULT bar folds them into one "Filters" button —
              they were four controls wrapping onto their own rows, which pushed
              the conversation list below the fold before an agent had read a
              single message. */}
          <div className="flex flex-wrap border-b border-gray-200 items-center gap-2 px-2 py-1.5 text-xs">
            {advanced ? (
              renderFilterControls(false)
            ) : (
              <div className="relative" ref={filtersMenuRef}>
                {/* Folding the controls away is only safe because nothing can
                    hide behind the button: every filter in here shows as a
                    removable chip below (and is named in the empty state) the
                    moment it is on, in BOTH modes. The badge counts that same
                    set — including the mine/all scope, which lives in the
                    always-visible tabs above rather than in here, because the
                    question it answers is "is anything narrowing my list?" */}
                <button
                  type="button"
                  onClick={() => setFiltersOpen((v) => !v)}
                  aria-expanded={filtersOpen}
                  title="Inbox, starred, label and team filters"
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${
                    activeFilters.length > 0
                      ? 'bg-primary-50 border-primary-300 text-primary-700 font-medium'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <SlidersHorizontal className="w-3 h-3" />
                  Filters
                  {activeFilters.length > 0 && (
                    <span className="ml-1 bg-primary-600 text-white text-[9px] rounded-full px-1.5">
                      {activeFilters.length}
                    </span>
                  )}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {filtersOpen && (
                  <div className="absolute top-full left-0 mt-1 z-30 flex flex-col items-start gap-2 min-w-[220px] rounded border border-gray-200 bg-white p-2 shadow-lg">
                    {renderFilterControls(true)}
                  </div>
                )}
              </div>
            )}
            {/* Sort stays on the row in both modes: it hides nothing, so it
                never belongs under a count of ACTIVE filters — and its current
                order is worth reading at a glance. */}
            <div className="relative ml-auto" ref={sortMenuRef}>
              <button
                type="button"
                onClick={() => setSortMenuOpen((v) => !v)}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                {SORT_LABELS[sort]}
                <ChevronDown className="w-3 h-3" />
              </button>
              {sortMenuOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-20 min-w-[160px] py-1">
                  {(Object.keys(SORT_LABELS) as SortOption[]).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => {
                        setSort(k);
                        setSortMenuOpen(false);
                      }}
                      className={`block w-full text-left px-3 py-1.5 hover:bg-gray-50 ${
                        sort === k ? 'text-primary-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {SORT_LABELS[k]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Active filter chips — every narrowing filter, not just labels. The
              inbox picker and the mine/all scope are remembered across reloads
              and live in controls you have to go looking for; without this row
              a list filtered down to nothing looks exactly like an empty queue. */}
          {activeFilters.length > 0 && (
            <div className="flex gap-1 flex-wrap items-center px-2 py-1.5 border-b border-gray-200 bg-gray-50">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                Filters
              </span>
              {activeFilters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={f.clear}
                  title={`Remove filter — ${f.chip}`}
                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                >
                  {f.chip} <X className="w-2.5 h-2.5" />
                </button>
              ))}
              {activeFilters.length > 1 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="ms-auto text-[10px] text-gray-500 hover:text-gray-700 underline"
                >
                  Clear all
                </button>
              )}
            </div>
          )}

          {/* Degraded: the label filter could not run server-side (composite
              index missing/still building, or too many combined filters), so
              only the loaded window was searched. A short list that is quietly
              short is indistinguishable from "nothing matched" — say it. */}
          {labelSearchDegraded && (
            <div className="border-b border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800">
              Searching loaded conversations only — older matches for this label may be missing. Load
              more to widen it.
            </div>
          )}

          {/* Bulk actions */}
          {selected.size > 0 && (
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex flex-wrap gap-1.5 items-center text-xs">
              <span className="font-semibold text-amber-900">{selected.size} selected</span>
              <button
                onClick={bulkResolve}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Check className="w-3 h-3" /> Resolve
              </button>
              <button
                onClick={bulkAssignToMe}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <UserCheck className="w-3 h-3" /> Assign me
              </button>
              <button
                onClick={bulkLabel}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Tag className="w-3 h-3" /> Label
              </button>
              {isSuperAdmin && (
                <button
                  onClick={bulkDelete}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-red-300 rounded text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              )}
              <button
                onClick={() => setSelected(new Set())}
                disabled={bulkBusy}
                className="px-2 py-1 text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            </div>
          )}

          {/* Conversation search */}
          <div className="border-b border-gray-200 px-2 py-1.5">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search conversations…"
                className="w-full pl-7 pr-7 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
            ) : visibleChats.length === 0 ? (
              <EmptyInbox
                // The tab as it is NAMED on screen — "Done", not "Resolved",
                // when the collapsed bar is up.
                label={labelForView(view, grouped)}
                search={search}
                filters={activeFilters}
                onClear={clearAllFilters}
              />
            ) : (
              <InboxList
                chats={visibleChats}
                selectedId={chatId}
                selected={selected}
                onToggleSelect={toggleSelect}
                onContextMenu={onRowContextMenu}
                hasMore={hasMore && !search}
                onLoadMore={loadMore}
                agentNames={agentNames}
                showSelect={advanced}
              />
            )}
          </div>
        </aside>

        <main
          className={`${chatId ? 'block' : 'hidden md:block'} overflow-hidden bg-gray-50 min-h-0 h-full`}
        >
          {chatId ? <Outlet /> : <EmptyState />}
        </main>
        </div>
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={buildConversationMenu({
            chat: menu.chat,
            selectedIds: [...selected],
            tenantCode: claims!.tenantCode!,
            agentUid: user!.uid,
            role: claims?.role,
            labels,
            teams,
            wpConfigured,
            advanced,
            navigate: (to: string) => navigate(to),
            clearSelection: () => setSelected(new Set()),
          })}
          onClose={() => setMenu(null)}
        />
      )}

      {composeOpen && claims?.tenantCode && user?.uid && (
        <NewConversationModal
          tenantCode={claims.tenantCode}
          agentUid={user.uid}
          onClose={() => setComposeOpen(false)}
          onCreated={(newChatId) => {
            setComposeOpen(false);
            navigate(`/inbox/${newChatId}`);
          }}
        />
      )}
    </div>
  );
}

// An empty queue that doesn't say WHY it is empty is indistinguishable from a
// broken one — this is the exact state that got reported as "conversations have
// disappeared" when the real cause was an inbox filter, remembered from a
// previous session, that nothing on screen mentioned. So: name every filter
// that is narrowing the list, and offer one button that lifts them all.
function EmptyInbox({
  label,
  search,
  filters,
  onClear,
}: {
  label: string;
  search: string;
  filters: ActiveFilter[];
  onClear: () => void;
}) {
  const headline = search
    ? `No conversations match “${search}”`
    : `No ${label === 'All' ? '' : `${label} `}conversations`;
  // "No In Progress (Human) conversations in inbox “Test states”, assigned to you."
  const narrowing = filters.map((f) => f.phrase).join(', ');
  const anything = filters.length > 0 || !!search;
  return (
    <div className="p-8 text-center">
      <InboxIcon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
      <p className="text-sm text-gray-500">
        {headline}
        {narrowing ? ` ${narrowing}` : ''}.
      </p>
      {anything && (
        <>
          <p className="mt-1 text-xs text-gray-400">
            {filters.length + (search ? 1 : 0)} filter
            {filters.length + (search ? 1 : 0) === 1 ? ' is' : 's are'} narrowing this list —
            conversations may be hidden.
          </p>
          <button
            type="button"
            onClick={onClear}
            className="mt-3 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-700 bg-white border border-primary-300 rounded hover:bg-primary-50"
          >
            <X className="w-3 h-3" /> Clear filters
          </button>
        </>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex items-center justify-center text-gray-400">
      <div className="text-center">
        <Plus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <div className="text-lg font-medium text-gray-500">Pick a conversation</div>
        <div className="text-sm mt-1 text-gray-400">Select one from the list to start replying.</div>
      </div>
    </div>
  );
}
